import {Howl, Howler} from 'howler';

const MAX_AUDIO_RETRIES = 2;
const HTML5_AUDIO_POOL_SIZE = 50;
const SILENT_AUDIO_SRC =
	'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
const AUDIO_UNLOCK_EVENTS = [
	'touchstart',
	'touchend',
	'pointerdown',
	'mousedown',
	'keydown',
	'click',
];

let currentSound = null;
let currentSoundEndHandler = null;
let activeSounds = [];
let unlockSource = SILENT_AUDIO_SRC;
let unlockHowl = null;
let unlockAudioElement = null;
let unlockAudioElementSource = null;
let unlockListenersBound = false;

function getRecoveryState(sound) {
	if (!sound.__literateRecoveryState) {
		sound.__literateRecoveryState = {
			playbackRequested: false,
			waitingForUnlock: false,
			retryingLoad: false,
			playRetries: 0,
			loadRetries: 0,
		};
	}

	return sound.__literateRecoveryState;
}

function isBrowser() {
	return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isHowlerUnlocked() {
	return Boolean(
		Howler._audioUnlocked ||
			(Howler.ctx && Howler.ctx.state === 'running')
	);
}

function removeUnlockListeners() {
	if (!isBrowser() || !unlockListenersBound) return;

	AUDIO_UNLOCK_EVENTS.forEach((eventName) => {
		document.removeEventListener(eventName, handleUnlockInteraction, true);
	});
	unlockListenersBound = false;
}

function handleUnlockInteraction() {
	unlockAudio();
}

function markUnlockedIfReady() {
	if (!isHowlerUnlocked()) return;

	removeUnlockListeners();
}

function resumeHowlerContext() {
	if (!Howler.ctx || typeof Howler.ctx.resume !== 'function') return;
	if (Howler.ctx.state === 'running') {
		markUnlockedIfReady();
		return;
	}

	const resumePromise = Howler.ctx.resume();
	if (resumePromise && typeof resumePromise.then === 'function') {
		resumePromise.then(markUnlockedIfReady).catch(() => {});
	}
}

function unlockNativeAudio() {
	if (!isBrowser() || typeof window.Audio === 'undefined') return;

	if (!unlockAudioElement) {
		unlockAudioElement = new window.Audio();
		unlockAudioElement.preload = 'auto';
		unlockAudioElement.setAttribute('playsinline', '');
		unlockAudioElement.setAttribute('webkit-playsinline', '');
	}

	if (unlockAudioElementSource !== unlockSource) {
		unlockAudioElement.src = unlockSource;
		unlockAudioElementSource = unlockSource;
	}

	try {
		unlockAudioElement.muted = false;
		unlockAudioElement.volume = 0.01;
	} catch (e) {}

	const playPromise = unlockAudioElement.play();
	if (playPromise && typeof playPromise.then === 'function') {
		playPromise
			.then(() => {
				try {
					unlockAudioElement.pause();
					unlockAudioElement.currentTime = 0;
				} catch (e) {}
			})
			.catch(() => {});
	} else {
		try {
			unlockAudioElement.pause();
			unlockAudioElement.currentTime = 0;
		} catch (e) {}
	}
}

function primeHowlerUnlock() {
	if (unlockHowl) return;

	unlockHowl = recoverableHowl(
		new Howl({
			src: [SILENT_AUDIO_SRC],
			html5: false,
			preload: true,
			volume: 0,
		})
	);
	unlockHowl.on('unlock', markUnlockedIfReady);
	unlockHowl.on('play', markUnlockedIfReady);
}

export function setupAudioUnlock(src = SILENT_AUDIO_SRC) {
	if (!isBrowser()) return;

	unlockSource = src || SILENT_AUDIO_SRC;
	Howler.autoSuspend = false;
	Howler.html5PoolSize = Math.max(
		Howler.html5PoolSize || 0,
		HTML5_AUDIO_POOL_SIZE
	);
	primeHowlerUnlock();

	if (!unlockListenersBound && !isHowlerUnlocked()) {
		AUDIO_UNLOCK_EVENTS.forEach((eventName) => {
			document.addEventListener(
				eventName,
				handleUnlockInteraction,
				true
			);
		});
		unlockListenersBound = true;
	}
}

export function unlockAudio(src = unlockSource) {
	if (!isBrowser()) return;

	setupAudioUnlock(src);
	resumeHowlerContext();
	unlockNativeAudio();

	if (unlockHowl && !unlockHowl.playing()) {
		try {
			unlockHowl.play();
		} catch (e) {}
	}

	markUnlockedIfReady();
}

export function recoverableHowl(sound) {
	if (!sound || sound.__literateRecoveryAttached) return sound;

	// Keep mobile/iPad audio recoverable after load/play failures.
	sound.__literateRecoveryAttached = true;
	const recovery = getRecoveryState(sound);

	sound.on('load', () => {
		const shouldPlayAfterRetry = recovery.retryingLoad;
		recovery.loadRetries = 0;
		recovery.retryingLoad = false;

		if (shouldPlayAfterRetry && recovery.playbackRequested) {
			window.setTimeout(() => {
				if (recovery.playbackRequested && !sound.playing()) {
					playHowl(sound);
				}
			}, 0);
		}
	});

	sound.on('loaderror', () => {
		if (recovery.loadRetries >= MAX_AUDIO_RETRIES) return;

		recovery.loadRetries += 1;
		recovery.retryingLoad = true;
		window.setTimeout(() => {
			if (sound.state && sound.state() !== 'loaded') {
				sound.load();
			}
		}, 250 * recovery.loadRetries);
	});

	sound.on('play', () => {
		recovery.playRetries = 0;
		recovery.waitingForUnlock = false;
	});

	sound.on('playerror', () => {
		// iPad Safari can block autoplay until Howler emits unlock.
		setupAudioUnlock();
		resumeHowlerContext();

		if (!recovery.waitingForUnlock) {
			recovery.waitingForUnlock = true;
			sound.once('unlock', () => {
				recovery.waitingForUnlock = false;
				if (recovery.playbackRequested && !sound.playing()) {
					playHowl(sound);
				}
			});
		}

		if (recovery.playRetries >= MAX_AUDIO_RETRIES) return;

		recovery.playRetries += 1;
		window.setTimeout(() => {
			if (recovery.playbackRequested && !sound.playing()) {
				playHowl(sound);
			}
		}, 300 * recovery.playRetries);
	});

	sound.on('end', () => {
		recovery.playbackRequested = false;
		unregisterSound(sound);
	});

	sound.on('stop', () => {
		recovery.playbackRequested = false;
	});

	sound.on('pause', () => {
		recovery.playbackRequested = false;
	});

	return sound;
}

export function createHowl(options) {
	setupAudioUnlock();

	return recoverableHowl(
		new Howl({
			// html5 mode streams through the OS audio pool instead of
			// decoding the whole clip into a JS-heap Web Audio buffer,
			// which iOS Safari/WebKit does not reliably free (crashes
			// the tab from memory pressure after a handful of plays).
			html5: true,
			preload: true,
			...options,
		})
	);
}

export function playHowl(sound) {
	if (!sound) return null;

	setupAudioUnlock();
	resumeHowlerContext();
	recoverableHowl(sound);
	const recovery = getRecoveryState(sound);
	recovery.playbackRequested = true;
	registerSound(sound);
	return sound.play();
}

function clearCurrentSoundEndHandler() {
	if (currentSound && currentSoundEndHandler) {
		currentSound.off('end', currentSoundEndHandler);
	}

	currentSoundEndHandler = null;
}

export function playSound(input, {onend} = {}) {
	if (!input) return null;

	if (currentSound) {
		clearCurrentSoundEndHandler();
		currentSound.stop();
		unregisterSound(currentSound);
		currentSound.unload(); // prevent memory leak (undisposed audio buffers crash iPad Safari after just a few plays)
		currentSound = null;
	}

	const sound =
		input instanceof Howl
			? recoverableHowl(input)
			: createHowl({
					src: Array.isArray(input) ? input : [input],
				});

	currentSoundEndHandler = () => {
		unregisterSound(sound);
		sound.unload();
		if (currentSound === sound) currentSound = null;
		currentSoundEndHandler = null;
		if (onend) onend();
	};
	sound.once('end', currentSoundEndHandler);

	currentSound = sound;
	return playHowl(sound);
}

export function stopAllSounds() {
	if (currentSound) {
		const soundToStop = currentSound;
		clearCurrentSoundEndHandler();
		currentSound = null;
		soundToStop.stop();
		unregisterSound(soundToStop);
		soundToStop.unload();
	}

	// Also stop AudioPlayer instances registered outside playSound().
	activeSounds.forEach((sound) => {
		if (sound) {
			sound.stop();
		}
	});
	activeSounds = [];
}

export function registerSound(sound) {
	if (sound && !activeSounds.includes(sound)) {
		activeSounds.push(sound);
	}
}

export function unregisterSound(sound) {
	activeSounds = activeSounds.filter((s) => s !== sound);
}
